import React, { useState } from "react";
import ModalTemplate from "../../components/templates/ModalTemplate";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckoutPayment from "./CheckoutPayment";
import { PaymentType } from "../../app/types/ProposalTypes";
import ConfirmationModal from "./ConfirmationModal";
import Button from "../../components/atoms/Button";

interface PaymentModalProps {
  open: boolean;
  handleClose: () => void;
  totalPrice?: number;
  depositAmount?: number;
  onPayment?: () => void;
  data: PaymentType[] | null;
  fontClass?: string;
  stylingType?: "events_styling" | "booking_proposal_styling";
}

function PaymentModal({
  open,
  handleClose,
  totalPrice = 0,
  onPayment,
  data,
  fontClass,
  stylingType = "events_styling",
}: PaymentModalProps) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const depositAmount = data ? data[0].payment_amount / 100 : 0;
  const remainingAmount = totalPrice - depositAmount;

  const handlePayment = () => {
    if (onPayment) {
      onPayment();
    }
  };

  const handleCancelClick = () => {
    setShowConfirmationModal(true);
  };

  return (
    <>
      <ModalTemplate
        open={open}
        handleClose={handleClose}
        className={`w-full max-w-2xl bg-white ${fontClass || "font-roboto"}`}
      >
        {/* Header */}
        <div className="p-4 flex flex-row justify-between items-center bg-white sticky top-0 z-10">
          <span className="font-semibold text-2xl">
            {globalThis.translate.em.proposal.payment_modal.modal_title}
          </span>
          <IconButton onClick={handleClose} className="h-8 w-8">
            <CloseIcon />
          </IconButton>
        </div>

        {/* Content */}
        <div className="p-6 bg-white flex flex-col gap-4 ">
          {/* Agreement signed confirmation */}
          <div className="flex items-center gap-3 mb-6 p-4 bg-green-50 rounded-lg text-[#2D2020]">
            <div>
              <div className="font-semibold flex gap-3 text-lg">
                <CheckCircleIcon className="text-[#658674] w-[21px] h-[21px]" />
                {
                  globalThis.translate.em.proposal.payment_modal
                    .agreement_signed
                }
              </div>
              <div className="text-lg font-normal">
                {globalThis.translate.em.proposal.payment_modal.thank_you}
              </div>
              <div className="text-lg font-normal">
                {globalThis.translate.em.proposal.payment_modal.next_step}
              </div>
            </div>
          </div>

          {/* Price overview */}
          <div className="mb-6">
            <h3 className="font-semibold text-2xl mb-4">
              {globalThis.translate.em.proposal.payment_modal.price_overview}
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center px-6">
                <span className="text-[#2D2020] font-normal text-lg">
                  {globalThis.translate.em.proposal.payment_modal.total}
                </span>
                <span className="font-normal text-lg text-[#2D2020]">
                  {totalPrice.toLocaleString("nb-NO")},-
                </span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center px-6">
                <span className="text-[#2D2020] font-normal text-lg">
                  {globalThis.translate.em.proposal.signature_page.deposit}
                </span>
                <span className="font-normal text-lg text-[#2D2020]">
                  {depositAmount.toLocaleString("nb-NO")},-
                </span>
              </div>
            </div>
          </div>

          <CheckoutPayment
            data={data}
            depositAmount={depositAmount}
            remainingAmount={remainingAmount}
          />
        </div>

        {/* Footer */}
        <div className="flex gap-1 bg-[#FAFAFA]">
          <div className="flex flex-row justify-end items-center w-full gap-3 px-6 py-4">
            <button
              onClick={handleCancelClick}
              className="py-2 px-6 font-medium text-gray-700 hover:bg-gray-50"
            >
              {globalThis.translate.em.proposal.payment_modal.cancel}
            </button>
            <Button
              onClick={handlePayment}
              className="bg-[#BF5934] py-2 px-6 rounded text-white font-medium hover:bg-[#A04426]"
              stylingType={stylingType}
            >
              {globalThis.translate.em.proposal.payment_modal.pay}
            </Button>
          </div>
        </div>
      </ModalTemplate>

      {/* Confirmation Modal */}
      <ConfirmationModal
        data={data}
        handleClose={handleClose}
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        fontClass={fontClass}
        stylingType={stylingType}
      />
    </>
  );
}

export default PaymentModal;
